import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import styled, { css, useTheme } from 'styled-components';
import { ISearchDB, TCreatorReportProcess } from 'type/db';

import { useGetCreatorReportList } from 'hooks/queries/creator';
import useSearchTable from 'hooks/table/useSearchTable';
import useGetSearchParams from 'hooks/useGetSearchParams';
import CoreLoading from 'shared/CoreLoading';
import CoreSelect from 'shared/CoreSelect';
import CoreSvg from 'shared/CoreSvg';
import Icon from 'shared/icons';
import style from 'styles/styled-components/style';
import { search_header_arr } from 'utils/table';

import SearchContentBoard from './SearchContentBoard';

const SearchResult = () => {
	const [groupId] = useGetSearchParams('groupId');
	const theme = useTheme();

	const navigate = useNavigate();

	const [activePk, setActivePk] = useState<number | null>(null);

	const match_cell_mapper = useSearchTable();

	const [missionProgress, setMissionProgress] = useState<TCreatorReportProcess>('all');

	const { data: creatorReports, isLoading: creatorReportsLoading } =
		useGetCreatorReportList(groupId, missionProgress);

	const rowClick = (row_data: ISearchDB) => {
		const pk = row_data.creatorPk;
		navigate(`/creator/ig/${pk}/${groupId}`);
	};

	const text = () => {
		return (
			<>
				{groupId}기의 연관계정 {creatorReports?.length} 건이 발견 되었습니다.
			</>
		);
	};

	const progressMapping: Record<string, TCreatorReportProcess> = {
		'전체 진행 상태': 'all',
		'제품 구매': 'productPurchase',
		'초안 전달': 'draftSend',
		'1차 미션': 'mission1',
		'2차 미션': 'mission2',
		'3차 미션': 'mission3',
	};

	const handleMissionStatusChange = (selectedOption: string) => {
		const progressValue = progressMapping[selectedOption] || '';
		setMissionProgress(progressValue);
	};

	return (
		<List>
			<Grid flex="between" _margin="8px 0 4px 0">
				<StateText>{text()}</StateText>
				<CoreSelect
					select_type={'box'}
					_height="35px"
					_width="170px"
					option_list={Object.keys(progressMapping)}
					selected_option={
						missionProgress === 'all'
							? '전체 진행 상태'
							: Object.keys(progressMapping).find(
									(key) => progressMapping[key] === missionProgress,
							  ) || '전체 진행 상태'
					}
					setSelectedOption={handleMissionStatusChange}
				/>
			</Grid>
			{/* 검색 결과 존재 */}
			{creatorReports !== null &&
				creatorReports?.length !== 0 &&
				!creatorReportsLoading && (
					<>
						<TableHeaderWrapper>
							{search_header_arr.map((item, idx) => (
								<React.Fragment key={idx}>
									<Grid
										_width={`${item.width}px`}
										flex="center"
										align="center"
										onHoverStart={() => {
											//setHover(idx);
										}}
										onHoverEnd={() => {
											//setHover(undefined);
										}}
									>
										<Text white_space="pre" typo="span">
											{item.header}
										</Text>
									</Grid>
									{/* 구분 선 */}
									{idx !== search_header_arr?.length - 1 && (
										<Contour _height="40%" _width="1px" />
									)}
								</React.Fragment>
							))}
						</TableHeaderWrapper>
						<TableResultWrapper>
							{creatorReports?.map((data: ISearchDB, row_idx: number) => (
								<React.Fragment key={row_idx}>
									<TableRow
										onClick={() => {
											rowClick(data);
										}}
										selected={false}
									>
										{search_header_arr.map((item, idx) => (
											<React.Fragment key={idx}>
												{match_cell_mapper[item.name](data, idx, row_idx)}
												{item.name === 'contents' && (
													<Grid
														flex="start"
														_padding="0 0 0 16px"
														_height="100%"
														onClick={(e) => {
															e.stopPropagation();
															if (activePk === data.creatorPk) {
																setActivePk(null);
															} else setActivePk(data.creatorPk);
														}}
													>
														{activePk === data.creatorPk ? (
															<CoreSvg>
																<Icon.DropArrowUp />
															</CoreSvg>
														) : (
															<CoreSvg>
																<Icon.DropArrowDown />
															</CoreSvg>
														)}
													</Grid>
												)}
											</React.Fragment>
										))}
									</TableRow>
									{activePk === data.creatorPk && (
										<SearchContentBoard
											values={data.contents}
											creatorPk={data.creatorPk}
										/>
									)}
								</React.Fragment>
							))}
						</TableResultWrapper>
					</>
				)}

			{/* 로딩 */}
			{creatorReportsLoading && (
				<Grid _height="calc(100vh - 400px)" flex="center">
					<CoreLoading color_type="black" />
				</Grid>
			)}
		</List>
	);
};
const { Grid, Text } = style;

const TableHeaderWrapper = styled(Grid)`
	height: 66px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	padding: 0 16px;

	${({ theme }) => theme.common.flexStart};

	div {
		cursor: pointer;
	}
`;

const TableResultWrapper = styled(Grid)`
	margin-bottom: 8px;
`;

const Contour = styled(Grid)`
	background-color: ${({ theme }) => theme.colors.gray.gray15};
`;
const TableRow = styled(Grid)<{ selected: boolean }>`
	min-width: calc(100% - 32px);
	height: 65px;

	border-radius: 8px;
	margin-top: 8px;
	padding: 0 16px;
	/* cursor: pointer; */

	${({ theme }) => theme.common.flexStart}

	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray_white};

	${({ theme, selected }) =>
		selected &&
		css`
			border: 1px solid ${theme.colors.gray.gray_black};
		`}

	&:hover {
		cursor: pointer;
		border-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		background-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		${({ theme, selected }) =>
			selected &&
			css`
				border-color: ${theme.colors.uplus.primary50_key};
			`}
	}
`;
const List = styled.div`
	width: 100%;
	height: max-content;
	max-width: 1080px;

	${({ theme }) => theme.common.flexStart};
	flex-direction: column;

	gap: 4px;
`;

const StateText = styled(Text)`
	${({ theme }) => theme.typo.body.span};
`;

export default SearchResult;
